/**
 * useSurface — reactive <html data-surface="dark"|"light">.
 *
 * The floating sidebar rail (Sidebar.jsx) needs to match the PAGE
 * underneath it, not the user's global theme toggle: AI Prompting and
 * Sales & Negotiation are unconditionally dark, Copywriting is
 * unconditionally light, regardless of what .dark says. data-surface is
 * the single source of truth the rail reads instead of the global
 * isDark flag — every other page never touches it, so it just mirrors
 * .dark for them by default.
 *
 * Three exports:
 *   useSurfaceSync()      — mount once (Sidebar.jsx does this) to keep
 *                            data-surface mirroring .dark by default.
 *   useForceSurface(v)    — a skill page with its own fixed treatment
 *                            calls this on mount with 'dark' | 'light';
 *                            restores the mirrored default on unmount.
 *   useSurface()          — reads data-surface reactively (Sidebar.jsx's
 *                            glass tokens key off this).
 */
import { useState, useEffect } from 'react';

let forceDepth = 0;

const syncToGlobalTheme = () => {
  if (forceDepth > 0) return;
  const isDark = document.documentElement.classList.contains('dark');
  document.documentElement.setAttribute('data-surface', isDark ? 'dark' : 'light');
};

export const useSurfaceSync = () => {
  useEffect(() => {
    syncToGlobalTheme();
    const obs = new MutationObserver(syncToGlobalTheme);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
};

export const useForceSurface = (value) => {
  useEffect(() => {
    forceDepth += 1;
    document.documentElement.setAttribute('data-surface', value);
    return () => {
      forceDepth -= 1;
      syncToGlobalTheme();
    };
  }, [value]);
};

export const useSurface = () => {
  const [surface, setSurface] = useState(() =>
    typeof window !== 'undefined'
      ? document.documentElement.getAttribute('data-surface') || 'light'
      : 'light'
  );
  useEffect(() => {
    const read = () => setSurface(document.documentElement.getAttribute('data-surface') || 'light');
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-surface'] });
    return () => obs.disconnect();
  }, []);
  return surface;
};
